<template>
  <div
    class="relative w-full pt-2 z-10"
    @click="isOptionsListVisible = !isOptionsListVisible"
    v-click-outside="clickOutside"
  >
    <div class="relative">
      <div class="label-primary flex gap-1.5">
        <span v-if="isChangeCase">{{ changeCase(label, ElementE.LABEL) }}</span>
        <span v-else>{{ label }}</span>
        <div
          v-if="label === 'Matrix display details'"
          class="-mt-0.5 bg-white"
        >
          <QuestionMarkIcon
            @mouseover="showTooltipTxt = true"
            @mouseleave="showTooltipTxt = false"
          />
          <div
            v-if="showTooltipTxt"
            class="absolute w-64 p-4 rounded-lg bg-white z-10 shadow-secondary"
          >
            <p class="tooltip-content">
              {{ tooltipTxt }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="h-[46px] p-3 border border-gray-300 rounded-lg text-sm text-neutral-125 cursor-default"
        :class="isOptionsListVisible && 'border-red-500'"
      >
        <component
          v-if="type === DropdownTypeE.ICON"
          :is="getAsyncComponent(selected.name)"
          class="icon"
        />
        <span v-else>{{ changeCase(selected.name, ElementE.DROPDOWN) }}</span>
      </div>
      <div
        class="absolute top-1/2 right-3 transform -translate-y-1/2 transition duration-300"
        :class="isOptionsListVisible ? '-rotate-90' : 'rotate-90'"
      >
        <DropdownMenuIcon />
      </div>
    </div>

    <ul
      v-if="isOptionsListVisible"
      class="absolute w-full mt-2 border border-gray-300 rounded-lg"
      :class="
        values.length > 3 ? 'h-[180px] overflow-y-scroll' : 'overflow-hidden'
      "
    >
      <li
        v-for="item in values"
        :key="item.id"
        :id="(item.id as string)"
        class="p-3 text-sm text-neutral-125 cursor-pointer hover:bg-neutral-0"
        :class="String(item.name) === selected.name ? 'bg-neutral-25' : 'bg-white'"
        @click="handleSelect(item)"
      >
        <component
          v-if="type === DropdownTypeE.ICON"
          :is="getAsyncComponent(item.name)"
          class="icon"
        />
        <span v-else>{{ changeCase(item.name, ElementE.DROPDOWN) }}</span>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, PropType } from "vue";
import DropdownMenuIcon from "../icons/RightArrowIcon.vue";
import QuestionMarkIcon from "../icons/QuestionMarkIcon.vue";
import { changeCase } from "../../helpers";
import { ElementE, DropdownTypeE } from '../../enums';
import { IShowDay, IShowVariant } from "../edittabs/constants";

export default defineComponent({
  name: "DropdownMenu",
  components: { DropdownMenuIcon, QuestionMarkIcon },
  props: {
    label: { type: String, required: true },
    defaultSelect: { type: String, default: "Select value" },
    values: {
      type: Array as () => (IShowDay | IShowVariant)[],
      default: () => [] as (IShowDay | IShowVariant)[],
    },
    isChangeCase: { type: Boolean, default: true },
    type: {
      type: String as PropType<DropdownTypeE>,
      default: DropdownTypeE.TEXT,
      validator: (value: string) => Object.values(DropdownTypeE).includes(value as DropdownTypeE)
    },
  },
  emits: ['select'],
  data() {
    return {
      isOptionsListVisible: false,
      selected: {
        id: '',
        name: this.defaultSelect,
      } as IShowDay | IShowVariant,
      showTooltipTxt: false,
      tooltipTxt:
        "Select the type of information shown in each cell of the matrix, such as available rooms, prices, or text.",

      changeCase: changeCase,
      ElementE: ElementE,
      DropdownTypeE,
    };
  },
  methods: {
    clickOutside() {
      this.isOptionsListVisible = false;
    },
    handleSelect(item: IShowDay | IShowVariant) {
      this.selected = item;
      this.$emit('select', item.id);
    },
    getAsyncComponent(name: string | number) {
      return defineAsyncComponent(() => import(`../icons/${name}.vue`));
    },
  },
});
</script>

<style>
.icon {
  @apply w-5 h-5;
}

.icon path {
  @apply fill-current;
}
</style>
